import { changedRatio, DIFF_COLLAPSE, wordsOf } from "@ext/git/core/Diff/logic/collapseThresholds";
import shouldCollapse from "@ext/git/core/Diff/logic/shouldCollapse";
import t from "@ext/localization/locale/translate";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node } from "prosemirror-model";
import { type EditorState, Plugin, PluginKey, type Transaction } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

// Expanded state must live in plugin state: the library rebuilds decorations on every edit,
// so state kept on nodes would collapse an expanded block on the next keystroke.
export const collapseDeletedKey = new PluginKey<Set<string>>("collapse-deleted");

const COLLAPSED_CLASS = "diff-deleted-collapsed";
const MARKER_CLASS = "diff-deleted-marker";
const HIT_CLASS = "diff-deleted-hit";
const CONTINUED_CLASS = "diff-deleted-continued";
const CONTINUES_CLASS = "diff-deleted-continues";
const CONTROLS = "button, a, input, select, textarea, [role='radio'], [role='tab'], .diff-deleted-restore";

const isControl = (target: EventTarget | null) =>
	target instanceof Element && !!target.closest(CONTROLS) && !target.closest(`.${MARKER_CLASS}`);
// A marker directly inside `tr` makes the browser wrap it in an anonymous cell (extra column),
// so row markers go into the first cell, which styles must not hide.
const MARKER_HOST_CLASS = "diff-marker-host";
const INLINE_COLLAPSIBLE_CLASS = "diff-inline-collapsible";
const INLINE_COLLAPSED_CLASS = "diff-inline-collapsed";
const INLINE_DELETED = ".diff-inline-deleted";
const INLINE_INSERTED = ".diff-inline-inserted";

export const TOP_LEVEL_DELETED = ".diff-deleted-block:not(.diff-deleted-block .diff-deleted-block)";

// Navigation must only expand: a toggle would collapse a deletion that is already open.
type CollapseDeletedMeta = string | { expand: string };

type DiffKey = { kind: "deleted" | "inline"; pos: number } | { kind: "column"; pos: number; column: number };

const KEY_PATTERN = /^(deleted|inline|column)-(\d+)(?:-(\d+))?$/;

const formatKey = (key: DiffKey): string =>
	key.kind === "column" ? `column-${key.pos}-${key.column}` : `${key.kind}-${key.pos}`;

const parseKey = (key: string): DiffKey | null => {
	const match = KEY_PATTERN.exec(key);
	if (!match) return null;
	const [, kind, pos, column] = match;
	if (kind === "column") return column === undefined ? null : { kind, pos: Number(pos), column: Number(column) };
	return column === undefined ? { kind: kind as "deleted" | "inline", pos: Number(pos) } : null;
};

const mapKey = (key: string, mapping: Transaction["mapping"]): string => {
	const parsed = parseKey(key);
	return parsed ? formatKey({ ...parsed, pos: mapping.map(parsed.pos) }) : key;
};

const mapExpanded = (expanded: Set<string>, mapping: Transaction["mapping"]): Set<string> => {
	const mapped = new Set([...expanded].map((key) => mapKey(key, mapping)));
	const unchanged = mapped.size === expanded.size && [...mapped].every((key) => expanded.has(key));
	return unchanged ? expanded : mapped;
};

export const isActivationKey = (event: KeyboardEvent) =>
	(event.key === "Enter" || event.key === " ") && !event.isComposing;

// Exact match first: nearest-only would mix up two adjacent deletions.
export const expandDeletedAt = (view: EditorView, pos: number): string | undefined => {
	const keys = [...view.dom.querySelectorAll<HTMLElement>(TOP_LEVEL_DELETED)]
		.map((block) => block.dataset.diffKey)
		.filter(Boolean)
		.map((key) => ({ key, parsed: parseKey(key) }))
		.filter((item) => item.parsed?.kind === "deleted")
		.map(({ key, parsed }) => ({ key, pos: parsed.pos }));

	if (!keys.length) return;

	const exact = keys.find((item) => item.pos === pos);
	const target =
		exact ?? keys.reduce((best, item) => (Math.abs(item.pos - pos) < Math.abs(best.pos - pos) ? item : best));

	view.dispatch(view.state.tr.setMeta(collapseDeletedKey, { expand: target.key }));
	return target.key;
};

// Don't key by change index: one change may render several elements and one element may cover several changes.
const keyOf = (view: EditorView, element: HTMLElement): string => {
	try {
		return formatKey({ kind: "deleted", pos: view.posAtDOM(element, 0) });
	} catch {
		return "";
	}
};

const BODY_CLASS = "diff-deleted-body";

// A bare text node ignores the grid child rules the collapse relies on and keeps the row open.
const wrapBareText = (block: HTMLElement) => {
	if (block.querySelector(`:scope > .${BODY_CLASS}`)) return;
	// Wrapping structural children would break `ul > li`; moving only text nodes would turn "A<br>B" into "AB<br>".
	const hasBareText = [...block.childNodes].some(
		(node) => node.nodeType === window.Node.TEXT_NODE && (node.textContent ?? "").trim() !== "",
	);
	if (!hasBareText) return;
	const inline = [...block.childNodes].filter(
		(node) => !(node instanceof HTMLElement && node.classList.contains(MARKER_CLASS)),
	);
	const body = document.createElement("span");
	body.className = BODY_CLASS;
	block.insertBefore(body, inline[0]);
	inline.forEach((node) => body.appendChild(node));
};

const TITLE_CLASS = "diff-deleted-title";
const TITLED = "tab-react-component[name]";

// Not `content: attr(name)`: generated content is invisible to page search, and `::before` holds the deletion stripe.
const showTitles = (block: HTMLElement) => {
	const titled = [block, ...block.querySelectorAll<HTMLElement>(TITLED)].filter(
		(node) => node.matches(TITLED) && !node.closest('[data-component="tabs"]'),
	);
	titled.forEach((node) => {
		if (node.querySelector(`:scope > .${TITLE_CLASS}`)) return;
		const text = node.getAttribute("name");
		if (!text) return;
		const title = document.createElement("span");
		title.className = TITLE_CLASS;
		title.contentEditable = "false";
		title.textContent = text;
		node.insertBefore(title, node.firstChild);
	});
};

const TEXTBLOCKS = "p, h1, h2, h3, h4, h5, h6, pre";

export const isInlineWidget = (block: HTMLElement) => !!block.parentElement?.closest(TEXTBLOCKS);

export type MarkerShiftTarget = { block: HTMLElement; marker?: HTMLElement };
export type MarkerShift = MarkerShiftTarget & { shift: string; span: string };

export const textLeftOf = (view: EditorView) => (view.dom.firstElementChild ?? view.dom).getBoundingClientRect().left;

// Must run on view update, not on marker creation: only a connected element can be measured.
export const measureMarkerShift = (textLeft: number, { block, marker }: MarkerShiftTarget): MarkerShift => {
	const box = block.getBoundingClientRect();
	const hosted = !!marker && marker.parentElement !== block;
	const spanWidth = hosted ? (block.closest("table") ?? block).getBoundingClientRect().width : 0;
	// A row marker sits in the first cell, so percentage widths would stop at that cell's edge.
	const span = hosted ? `${Math.round(spanWidth)}px` : "";
	return { block, marker, shift: `${Math.round(box.left - textLeft)}px`, span };
};

export const writeMarkerShift = ({ block, marker, shift, span }: MarkerShift) => {
	// Set on the block: its pseudo-element stripe reads the same variable the marker inherits.
	if (block.style.getPropertyValue("--diff-marker-shift") !== shift) {
		block.style.setProperty("--diff-marker-shift", shift);
	}
	if (marker && marker.style.getPropertyValue("--diff-marker-span") !== span) {
		marker.style.setProperty("--diff-marker-span", span);
	}
};

const TAB_CHIP_CLASS = "diff-deleted-tab";

const showDeletedTabs = (view: EditorView) => {
	view.dom.querySelectorAll<HTMLElement>('[data-component="tabs"]').forEach((block) => {
		if (block.closest(".diff-deleted-block")) return;
		const strip = block.querySelector<HTMLElement>(".cases-container");
		if (!strip) return;
		const names = [...block.querySelectorAll<HTMLElement>(".diff-deleted-block.node-tab")]
			.map((node) => node.getAttribute("name") ?? "")
			.filter(Boolean);
		const chips = [...strip.querySelectorAll<HTMLElement>(`.${TAB_CHIP_CLASS}`)];
		chips.forEach((chip) => {
			if (!names.includes(chip.textContent ?? "")) chip.remove();
		});
		names.forEach((name) => {
			if (chips.some((chip) => chip.textContent === name)) return;
			const chip = document.createElement("span");
			chip.className = TAB_CHIP_CLASS;
			chip.contentEditable = "false";
			chip.title = t("diff.deleted.tab");
			chip.textContent = name;
			strip.appendChild(chip);
		});
	});
};

const markerHost = (block: HTMLElement): HTMLElement => {
	if (block.tagName !== "TR") return block;
	const cell = block.querySelector<HTMLElement>(":scope > td, :scope > th");
	if (!cell) return block;
	cell.classList.add(MARKER_HOST_CLASS);
	const stale = block.querySelector<HTMLElement>(`:scope > .${MARKER_CLASS}`);
	if (stale) cell.insertBefore(stale, cell.firstChild);
	return cell;
};

const DELETED_CELL = ".diff-deleted-cell";
const CELL_COLLAPSED_CLASS = "diff-deleted-cell-collapsed";
const ROW_CELL_IN_COLUMN_CLASS = "diff-deleted-column-cell";
export const COLUMN_MARKER_CLASS = "diff-column-marker";

const columnKeyOf = (view: EditorView, cell: HTMLTableCellElement): string => {
	const table = cell.closest("table");
	if (!table) return "";
	try {
		return formatKey({ kind: "column", pos: view.posAtDOM(table, 0), column: cell.cellIndex });
	} catch {
		return "";
	}
};

const columnMarker = (cell: HTMLElement): HTMLElement => {
	const existing = cell.querySelector<HTMLElement>(`:scope > .${COLUMN_MARKER_CLASS}`);
	if (existing) return existing;
	const marker = document.createElement("span");
	marker.className = COLUMN_MARKER_CLASS;
	marker.setAttribute("role", "button");
	marker.setAttribute("tabindex", "0");
	marker.setAttribute("aria-label", t("diff.deleted.column"));
	marker.contentEditable = "false";
	cell.insertBefore(marker, cell.firstChild);
	return marker;
};

// One key per column: a deleted column opens and closes as a whole, its marker sits in the top cell.
const applyDeletedColumns = (view: EditorView, expanded: Set<string>) => {
	const marked = new Set<string>();
	view.dom.querySelectorAll<HTMLTableCellElement>(DELETED_CELL).forEach((cell) => {
		const key = columnKeyOf(view, cell);
		if (!key) return;
		const isExpanded = expanded.has(key);
		cell.dataset.diffKey = key;
		cell.classList.toggle(CELL_COLLAPSED_CLASS, !isExpanded);

		if (marked.has(key)) {
			cell.querySelector(`:scope > .${COLUMN_MARKER_CLASS}`)?.remove();
			return;
		}
		marked.add(key);
		const marker = columnMarker(cell);
		marker.dataset.diffKey = key;
		marker.setAttribute("aria-expanded", String(isExpanded));
	});

	// A deleted row still holds its cell of the deleted column; it must fold with the column, not squeeze into it.
	view.dom.querySelectorAll<HTMLTableCellElement>(`tr${TOP_LEVEL_DELETED} > :is(td, th)`).forEach((cell) => {
		const key = columnKeyOf(view, cell);
		const inColumn = marked.has(key);
		cell.classList.toggle(ROW_CELL_IN_COLUMN_CLASS, inColumn);
		cell.classList.toggle(CELL_COLLAPSED_CLASS, inColumn && !expanded.has(key));
	});
};

const TABLE_SCROLLER = ".scrollableContent";
const MARKER_ROOM_CLASS = "diff-marker-room";
const MARKER_ROOM_VAR = "--diff-marker-room";
const OWN_MARGIN_VAR = "--diff-marker-own-margin";
const MARKER_REACH = 8;

type MarkerRoom = { scrollers: Set<HTMLElement>; rooms: { scroller: HTMLElement; ownMargin: string; room: number }[] };

// A table scroller clips horizontally, and row markers sit on the article axis to its left.
const measureMarkerRoom = (view: EditorView, textLeft: number): MarkerRoom => {
	const dom = view.dom as HTMLElement;
	const scrollers = new Set<HTMLElement>();
	dom.querySelectorAll<HTMLElement>(`tr${TOP_LEVEL_DELETED}`).forEach((row) => {
		const scroller = row.closest<HTMLElement>(TABLE_SCROLLER);
		if (scroller && dom.contains(scroller)) scrollers.add(scroller);
	});
	if (!scrollers.size) return { scrollers, rooms: [] };

	const axis = Number.parseFloat(getComputedStyle(dom).getPropertyValue("--diff-column-axis")) || 0;
	const rooms = [...scrollers].map((scroller) => {
		const current = Number.parseFloat(scroller.style.getPropertyValue(MARKER_ROOM_VAR)) || 0;
		const ownMargin = scroller.classList.contains(MARKER_ROOM_CLASS)
			? scroller.style.getPropertyValue(OWN_MARGIN_VAR)
			: getComputedStyle(scroller).marginLeft;
		const naturalLeft = scroller.getBoundingClientRect().left + current;
		const room = Math.max(0, Math.ceil(naturalLeft - (textLeft + axis - MARKER_REACH)));
		return { scroller, ownMargin, room };
	});
	return { scrollers, rooms };
};

const writeMarkerRoom = (view: EditorView, { scrollers, rooms }: MarkerRoom) => {
	view.dom.querySelectorAll<HTMLElement>(`.${MARKER_ROOM_CLASS}`).forEach((scroller) => {
		if (scrollers.has(scroller)) return;
		scroller.classList.remove(MARKER_ROOM_CLASS);
		scroller.style.removeProperty(MARKER_ROOM_VAR);
		scroller.style.removeProperty(OWN_MARGIN_VAR);
	});
	rooms.forEach(({ scroller, ownMargin, room }) => {
		scroller.classList.toggle(MARKER_ROOM_CLASS, room > 0);
		const value = room > 0 ? `${room}px` : "";
		if (scroller.style.getPropertyValue(MARKER_ROOM_VAR) !== value)
			scroller.style.setProperty(MARKER_ROOM_VAR, value);
		if (scroller.style.getPropertyValue(OWN_MARGIN_VAR) !== ownMargin)
			scroller.style.setProperty(OWN_MARGIN_VAR, ownMargin);
	});
};

const SETTLING_CLASS = "diff-deleted-settling";

const settleWithoutTransition = (block: HTMLElement) => {
	block.classList.add(SETTLING_CLASS);
	requestAnimationFrame(() => requestAnimationFrame(() => block.classList.remove(SETTLING_CLASS)));
};

type Run = { previous: HTMLElement; block: HTMLElement };

const measureRunGaps = (runs: Run[]): string[] =>
	runs.map(({ previous, block }) => {
		const above = Number.parseFloat(getComputedStyle(previous).marginBottom) || 0;
		const below = Number.parseFloat(getComputedStyle(block).marginTop) || 0;
		return `${Math.round(Math.max(above, below))}px`;
	});

const writeRunGaps = (runs: Run[], gaps: string[]) => {
	runs.forEach(({ block }, index) => {
		if (block.style.getPropertyValue("--diff-run-gap") !== gaps[index]) {
			block.style.setProperty("--diff-run-gap", gaps[index]);
		}
	});
};

const createMarker = () => {
	const marker = document.createElement("span");
	marker.className = MARKER_CLASS;
	marker.setAttribute("role", "button");
	marker.setAttribute("tabindex", "0");
	marker.setAttribute("aria-label", t("diff.type.deleted"));
	// Without this the caret enters the marker and Backspace deletes it.
	marker.contentEditable = "false";
	const hit = document.createElement("span");
	hit.className = HIT_CLASS;
	marker.appendChild(hit);
	return marker;
};

const markDeletedBlocks = (view: EditorView, expanded: Set<string>) => {
	const runs: Run[] = [];
	const shifts: MarkerShiftTarget[] = [];
	let previous: { key: string; block: HTMLElement } | null = null;

	const blocks = [...view.dom.querySelectorAll<HTMLElement>(TOP_LEVEL_DELETED)];
	blocks.forEach((block) => {
		if (isInlineWidget(block)) return;
		const key = keyOf(view, block);
		if (!key) return;

		if (!block.dataset.diffKey) settleWithoutTransition(block);
		block.dataset.diffKey = key;
		const isExpanded = expanded.has(key);
		block.classList.toggle(COLLAPSED_CLASS, !isExpanded);
		wrapBareText(block);
		showTitles(block);

		const host = markerHost(block);
		const continued = previous?.key === key;
		block.classList.toggle(CONTINUED_CLASS, continued);
		if (continued && previous) {
			host.querySelector(`:scope > .${MARKER_CLASS}`)?.remove();
			runs.push({ previous: previous.block, block });
			previous = { key, block };
			shifts.push({ block });
			return;
		}
		previous = { key, block };

		// Look up the marker where it is inserted, or every transaction adds a new one.
		let marker = host.querySelector<HTMLElement>(`:scope > .${MARKER_CLASS}`);
		if (!marker) {
			marker = createMarker();
			host.insertBefore(marker, host.firstChild);
		}

		marker.setAttribute("aria-expanded", String(isExpanded));
		marker.dataset.diffKey = key;
		shifts.push({ block, marker });
	});

	const continues = new Set(runs.map(({ previous }) => previous));
	blocks.forEach((block) => {
		block.classList.toggle(CONTINUES_CLASS, continues.has(block));
	});
	return { runs, shifts };
};

// Reads first, then writes: interleaving them forces a layout per marker.
const layoutMarkers = (view: EditorView, runs: Run[], shifts: MarkerShiftTarget[]) => {
	const textLeft = textLeftOf(view);
	const measuredShifts = shifts.map((target) => measureMarkerShift(textLeft, target));
	const gaps = measureRunGaps(runs);
	const room = measureMarkerRoom(view, textLeft);

	measuredShifts.forEach(writeMarkerShift);
	writeRunGaps(runs, gaps);
	writeMarkerRoom(view, room);
};

// Column markers, cell classes and tab chips live in the article's own DOM and outlive the diff decorations.
const clearDiffDom = (view: EditorView) => {
	view.dom.querySelectorAll(`.${COLUMN_MARKER_CLASS}, .${TAB_CHIP_CLASS}`).forEach((node) => node.remove());
	view.dom.querySelectorAll<HTMLElement>(`.${CELL_COLLAPSED_CLASS}, .${ROW_CELL_IN_COLUMN_CLASS}`).forEach((cell) => {
		cell.classList.remove(CELL_COLLAPSED_CLASS, ROW_CELL_IN_COLUMN_CLASS);
		delete cell.dataset.diffKey;
	});
	writeMarkerRoom(view, { scrollers: new Set(), rooms: [] });
};

type Range = { from: number; to: number };

const leafBlocks = (node: Node): { node: Node; offset: number }[] => {
	if (node.isTextblock) return [{ node, offset: 0 }];
	const leaves: { node: Node; offset: number }[] = [];
	node.descendants((child, pos) => {
		if (!child.isTextblock) return true;
		leaves.push({ node: child, offset: pos + 1 });
		return false;
	});
	return leaves;
};

// Changes straight from the library (`storage.diff`) have no `from`, only `posB`; without the fallback nothing collapses.
const collapsibleRanges = (change: DiffChange, threshold: number | undefined): Range[] => {
	const from = change.from ?? change.posB;
	if (typeof from !== "number") return [];
	const whole = (): Range[] =>
		shouldCollapse(change, threshold) ? [{ from, to: change.to ?? from + (change.nodeB?.nodeSize ?? 0) }] : [];

	const nodeA = change.nodeA as Node | undefined;
	const nodeB = change.nodeB as Node | undefined;
	if (!nodeA?.descendants || !nodeB?.descendants) return whole();

	const leavesA = leafBlocks(nodeA);
	const leavesB = leafBlocks(nodeB);
	if (!leavesB.length || !leavesA.length) return whole();

	const taken = new Set<number>();
	return leavesB.flatMap((leaf, position) => {
		let bestIndex = -1;
		let bestRatio = 1;
		leavesA.forEach((candidate, index) => {
			if (taken.has(index)) return;
			const ratio = changedRatio(wordsOf(candidate.node.textContent), wordsOf(leaf.node.textContent));
			if (ratio < bestRatio) {
				bestRatio = ratio;
				bestIndex = index;
			}
		});
		if (bestIndex < 0) {
			const remainingNew = leavesB.length - position;
			const remainingOld = leavesA.length - taken.size;
			if (remainingOld < remainingNew) return [];
			bestIndex = leavesA.findIndex((_, index) => !taken.has(index));
		}
		taken.add(bestIndex);

		const pair = { type: "modified", nodeA: leavesA[bestIndex].node, nodeB: leaf.node } as DiffChange;
		if (!shouldCollapse(pair, threshold)) return [];
		const start = from + leaf.offset;
		return [{ from: start, to: start + leaf.node.nodeSize }];
	});
};

const oldWordsOf = (inline: HTMLElement): number => {
	const block = inline.closest(TEXTBLOCKS);
	if (!block) return Number.POSITIVE_INFINITY;
	const inserted = [...block.querySelectorAll(INLINE_INSERTED)].reduce(
		(count, node) => count + wordsOf(node.textContent ?? "").length,
		0,
	);
	return wordsOf(block.textContent ?? "").length - inserted;
};

const INLINE_TOGGLE_ATTRS = ["aria-expanded", "role", "tabindex", "aria-label"];

// Never insert a marker element inside a paragraph: a foreign node in a ProseMirror textblock breaks `posAtDOM`.
const applyInlineThresholds = (
	view: EditorView,
	changes: DiffChange[],
	expanded: Set<string>,
	threshold: number | undefined,
) => {
	const collapsible = changes
		.filter((change) => change.type === "modified")
		.flatMap((change) => collapsibleRanges(change, threshold));

	view.dom.querySelectorAll<HTMLElement>(INLINE_DELETED).forEach((inline) => {
		let blockPos: number | undefined;
		try {
			blockPos = view.posAtDOM(inline, 0);
		} catch {
			return;
		}

		const deletedWords = wordsOf(inline.textContent ?? "").length;
		const oldWords = oldWordsOf(inline);
		// Must test the whole range, not just "after start", or an early collapsed paragraph hides edits below it.
		const inside =
			oldWords >= DIFF_COLLAPSE.minParagraphWords &&
			(deletedWords >= DIFF_COLLAPSE.longDeletionWords ||
				deletedWords / oldWords > (threshold ?? DIFF_COLLAPSE.ratio) ||
				collapsible.some((range) => blockPos !== undefined && blockPos >= range.from && blockPos <= range.to));
		inline.classList.toggle(INLINE_COLLAPSIBLE_CLASS, inside);

		if (!inside) {
			inline.classList.remove(INLINE_COLLAPSED_CLASS);
			delete inline.dataset.diffKey;
			INLINE_TOGGLE_ATTRS.forEach((name) => inline.removeAttribute(name));
			return;
		}

		const key = formatKey({ kind: "inline", pos: blockPos });
		inline.dataset.diffKey = key;
		inline.classList.toggle(INLINE_COLLAPSED_CLASS, !expanded.has(key));
		inline.setAttribute("aria-expanded", String(expanded.has(key)));
		inline.setAttribute("role", "button");
		inline.setAttribute("tabindex", "0");
		inline.setAttribute("aria-label", t("diff.inline.deleted"));
	});
};

// `getThreshold` is a getter read on every view update so threshold changes apply without remounting the editor.
const collapseDeleted = (getChanges: () => DiffChange[] | undefined, getThreshold?: () => number | undefined) =>
	new Plugin<Set<string>>({
		key: collapseDeletedKey,
		state: {
			init: () => new Set<string>(),
			apply: (tr, expanded) => {
				// No changes means the diff is off: keys from the previous diff would open unrelated deletions later.
				if (!getChanges()?.length) return expanded.size ? new Set<string>() : expanded;

				const current = tr.docChanged ? mapExpanded(expanded, tr.mapping) : expanded;
				const meta = tr.getMeta(collapseDeletedKey) as CollapseDeletedMeta | undefined;
				if (!meta) return current;

				const next = new Set(current);
				if (typeof meta !== "string") {
					next.add(meta.expand);
					return next;
				}

				if (next.has(meta)) next.delete(meta);
				else next.add(meta);
				return next;
			},
		},
		view: (view) => {
			let cleared = false;
			let renderedChanges: DiffChange[] | undefined;
			let renderedThreshold: number | undefined;

			const refresh = (target: EditorView) => {
				const changes = getChanges();
				const threshold = getThreshold?.();
				renderedChanges = changes;
				renderedThreshold = threshold;

				if (!changes?.length) {
					if (!cleared) clearDiffDom(target);
					cleared = true;
					return;
				}
				cleared = false;

				const expanded = collapseDeletedKey.getState(target.state) ?? new Set<string>();
				const { runs, shifts } = markDeletedBlocks(target, expanded);
				applyDeletedColumns(target, expanded);
				applyInlineThresholds(target, changes, expanded, threshold);
				showDeletedTabs(target);
				layoutMarkers(target, runs, shifts);
			};

			const isStale = (target: EditorView, prevState: EditorState) =>
				target.state.doc !== prevState.doc ||
				collapseDeletedKey.getState(target.state) !== collapseDeletedKey.getState(prevState) ||
				getChanges() !== renderedChanges ||
				getThreshold?.() !== renderedThreshold;

			const toggle = (key: string) => view.dispatch(view.state.tr.setMeta(collapseDeletedKey, key));

			// Toggle on click, not mousedown, or pressing inside an expanded deletion collapses it before a selection drag.
			const onClick = (event: MouseEvent) => {
				if (event.button !== 0) return;

				const target = (event.target as HTMLElement)?.closest<HTMLElement>(
					`.${MARKER_CLASS}, .${COLUMN_MARKER_CLASS}, .${CELL_COLLAPSED_CLASS}, ${TOP_LEVEL_DELETED}, .${INLINE_COLLAPSIBLE_CLASS}`,
				);
				if (!target) return;
				const isMarker =
					target.classList.contains(MARKER_CLASS) || target.classList.contains(COLUMN_MARKER_CLASS);
				if (!isMarker && isControl(event.target)) return;

				if (window.getSelection()?.toString()) return;

				const key = target.dataset.diffKey;
				if (!key) return;

				toggle(key);
			};

			// Capture phase: ProseMirror's own keydown handler on the same element would split a block on Enter.
			const onKeyDown = (event: KeyboardEvent) => {
				if (!isActivationKey(event) || !(event.target instanceof Element)) return;
				const marker = event.target.closest<HTMLElement>(
					`.${MARKER_CLASS}, .${COLUMN_MARKER_CLASS}, .${INLINE_COLLAPSIBLE_CLASS}`,
				);
				const key = marker?.dataset.diffKey;
				if (!key) return;

				event.preventDefault();
				event.stopPropagation();
				toggle(key);
			};

			const onMouseDown = (event: MouseEvent) => {
				const marker = (event.target as HTMLElement)?.closest(
					`.${MARKER_CLASS}, .${COLUMN_MARKER_CLASS}, .${CELL_COLLAPSED_CLASS}, .${INLINE_COLLAPSED_CLASS}`,
				);
				if (marker) event.preventDefault();
			};

			view.dom.addEventListener("mousedown", onMouseDown);
			view.dom.addEventListener("click", onClick);
			view.dom.addEventListener("keydown", onKeyDown, true);
			refresh(view);

			const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => refresh(view));
			observer?.observe(view.dom);

			return {
				update: (updatedView, prevState) => {
					if (isStale(updatedView, prevState)) refresh(updatedView);
				},
				destroy: () => {
					observer?.disconnect();
					view.dom.removeEventListener("mousedown", onMouseDown);
					view.dom.removeEventListener("click", onClick);
					view.dom.removeEventListener("keydown", onKeyDown, true);
				},
			};
		},
	});

export default collapseDeleted;
